import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/shared/config/firebase';
import { useAuth } from '@/app/providers/AuthContext';

interface UserData {
  id: string;
  email: string;
  isSuperAdmin: boolean;
}

/**
 * Hook pour récupérer les données utilisateur avec isSuperAdmin
 */
export const useUserData = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.email) {
        setUserData(null);
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Chercher le document customer par email
        const customersRef = collection(db, 'customers');
        const q = query(customersRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const data = userDoc.data();
          
          const user: UserData = {
            id: userDoc.id,
            email: data.email || currentUser.email || '',
            isSuperAdmin: data.isSuperAdmin || false,
          };
          setUserData(user);
          setIsSuperAdmin(user.isSuperAdmin);
        } else {
          // Si le document n'existe pas, créer avec les valeurs par défaut
          setUserData({
            id: currentUser.uid,
            email: currentUser.email || '',
            isSuperAdmin: false,
          });
          setIsSuperAdmin(false);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        setUserData(null);
        setIsSuperAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser?.email, currentUser?.uid]);

  return {
    userData,
    isSuperAdmin,
    loading,
  };
};
